import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CancelPresentationRoundedIcon from '@mui/icons-material/CancelPresentationRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import { useNotification } from './context/NotificationContext';

const BASE_URL = 'https://frek.cc/proxy/10.147.19.105:5000';
const DEFAULT_PRINT_FORM = {
  copies: '1',
  page_range: '',
  color_mode: 'RGB',
};
const AUTO_REFRESH_MS = 10000;
const INITIAL_CONFIRM_STATE = {
  open: false,
  title: '',
  description: '',
  confirmLabel: 'Confirm',
  intent: 'default',
  icon: 'default',
  details: [],
  onConfirm: null,
};

function formatBytes(size) {
  const numericSize = Number(size || 0);
  if (!numericSize) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  let value = numericSize;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function getConfirmIcon(icon) {
  switch (icon) {
    case 'print':
      return <PrintRoundedIcon />;
    case 'delete':
      return <DeleteOutlineRoundedIcon />;
    case 'cancel':
      return <CancelPresentationRoundedIcon />;
    case 'history':
      return <HistoryRoundedIcon />;
    default:
      return <HelpOutlineRoundedIcon />;
  }
}

function getStatusColor(status) {
  if (status === 'completed') {
    return 'success';
  }
  if (status === 'processing') {
    return 'warning';
  }
  if (status === 'canceled' || status === 'failed') {
    return 'error';
  }
  return 'default';
}

function PrinterDashboard() {
  const { showNotification } = useNotification();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [files, setFiles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [health, setHealth] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [expandedFile, setExpandedFile] = useState('');
  const [printForms, setPrintForms] = useState({});
  const [busyKey, setBusyKey] = useState('');
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState('');
  const [confirmState, setConfirmState] = useState(INITIAL_CONFIRM_STATE);

  const darkTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'dark',
          background: {
            default: '#0b0b10',
            paper: '#15161b',
          },
          text: {
            primary: '#e8e9ee',
            secondary: 'rgba(255,255,255,0.65)',
          },
          primary: {
            main: '#90caf9',
          },
          secondary: {
            main: '#7bdff2',
          },
          success: {
            main: '#4caf50',
          },
          warning: {
            main: '#ffb74d',
          },
          error: {
            main: '#f44336',
          },
        },
        shape: {
          borderRadius: 12,
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                borderRadius: 10,
                fontWeight: 600,
              },
            },
          },
        },
      }),
    []
  );

  const handleNotification = useCallback(
    (message, severity = 'success') => {
      const normalizedMessage = String(message ?? '')
        .replace(/\s+/g, ' ')
        .trim();
      const limitedMessage =
        normalizedMessage.length > 220
          ? `${normalizedMessage.slice(0, 217)}...`
          : normalizedMessage;
      showNotification(limitedMessage, severity);
    },
    [showNotification]
  );

  const requestJson = useCallback(async (path, options = {}) => {
    const response = await fetch(`${BASE_URL}${path}`, options);
    const contentType = response.headers.get('content-type') || '';
    const body = contentType.includes('application/json')
      ? await response.json()
      : { error: await response.text() };

    if (!response.ok) {
      throw new Error(body.error || body.message || `Request failed with status ${response.status}`);
    }

    return body;
  }, []);

  const closeConfirmDialog = () => {
    setConfirmState(INITIAL_CONFIRM_STATE);
  };



  
  const openConfirmDialog = ({ title, description, confirmLabel, intent, icon, details, onConfirm }) => {
    setConfirmState({
      open: true,
      title,
      description,
      confirmLabel,
      intent: intent || 'default',
      icon: icon || intent || 'default',
      details: details || [],
      onConfirm,
    });
  };

  const refreshJobsSnapshot = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setBusyKey('refresh-jobs');
      } else {
        setIsAutoRefreshing(true);
      }

      try {
        const [jobsResponse, healthResponse] = await Promise.all([
          requestJson('/api/jobs'),
          requestJson('/health'),
        ]);
        setJobs(jobsResponse.jobs || []);
        setHealth(healthResponse);
        setLastUpdated(new Date().toLocaleTimeString());
        if (!silent) {
          handleNotification('Print job statuses refreshed.');
        }
      } catch (requestError) {
        if (!silent) {
          handleNotification(requestError.message, 'error');
        }
      } finally {
        if (!silent) {
          setBusyKey('');
        } else {
          setIsAutoRefreshing(false);
        }
      }
    },
    [handleNotification, requestJson]
  );

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [healthResponse, filesResponse, jobsResponse] = await Promise.all([
        requestJson('/health'),
        requestJson('/api/files'),
        requestJson('/api/jobs'),
      ]);

      setHealth(healthResponse);
      setFiles(filesResponse.files || []);
      setJobs(jobsResponse.jobs || []);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (requestError) {
      handleNotification(requestError.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [handleNotification, requestJson]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      refreshJobsSnapshot({ silent: true });
    }, AUTO_REFRESH_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [refreshJobsSnapshot]);

  const updatePrintForm = (storedName, field, value) => {
    setPrintForms((current) => ({
      ...current,
      [storedName]: {
        ...(current[storedName] || DEFAULT_PRINT_FORM),
        [field]: value,
      },
    }));
  };

  const getPrintForm = (storedName) => printForms[storedName] || DEFAULT_PRINT_FORM;

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!uploadFile) {
      handleNotification('Choose a file before uploading.', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadFile);

    setUploading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/upload`, {
        method: 'POST',
        body: formData,
      });
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body.error || 'Upload failed.');
      }

      setUploadFile(null);
      handleNotification(body.message || 'File uploaded successfully.');
      await loadData();

      const input = document.getElementById('printer-upload-input');
      if (input) {
        input.value = '';
      }
    } catch (uploadError) {
      handleNotification(uploadError.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const executePrint = async (storedName) => {
    const form = getPrintForm(storedName);
    setBusyKey(`print:${storedName}`);

    try {
      const response = await requestJson('/api/print', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stored_name: storedName,
          copies: form.copies || '1',
          page_range: form.page_range || '',
          color_mode: form.color_mode || 'RGB',
        }),
      });
      handleNotification(response.message || 'Print job sent.');
      await loadData();
    } catch (printError) {
      handleNotification(printError.message, 'error');
    } finally {
      setBusyKey('');
    }
  };

  const askPrintConfirmation = (file) => {
    const form = getPrintForm(file.stored_name);
    openConfirmDialog({
      title: 'Send file to printer',
      description: `Print "${file.original_name}" now?`,
      confirmLabel: 'Print',
      intent: 'primary',
      icon: 'print',
      details: [
        `File: ${file.original_name}`,
        `Copies: ${form.copies || '1'}`,
        `Pages: ${form.page_range || 'All pages'}`,
        `Mode: ${form.color_mode === 'Gray' ? 'Black and White' : 'Color'}`,
      ],
      onConfirm: async () => {
        closeConfirmDialog();
        await executePrint(file.stored_name);
      },
    });
  };

  const executeDeleteFile = async (storedName) => {
    setBusyKey(`delete:${storedName}`);

    try {
      const response = await requestJson(`/api/files/${encodeURIComponent(storedName)}`, {
        method: 'DELETE',
      });
      handleNotification(response.message || 'File deleted.');
      await loadData();
    } catch (deleteError) {
      handleNotification(deleteError.message, 'error');
    } finally {
      setBusyKey('');
    }
  };

  const askDeleteConfirmation = (file) => {
    openConfirmDialog({
      title: 'Delete uploaded file',
      description: `Delete "${file.original_name}" from the printer backend storage?`,
      confirmLabel: 'Delete',
      intent: 'danger',
      icon: 'delete',
      details: [
        `File: ${file.original_name}`,
        `Type: ${file.kind.toUpperCase()}`,
        `Size: ${formatBytes(file.size)}`,
      ],
      onConfirm: async () => {
        closeConfirmDialog();
        await executeDeleteFile(file.stored_name);
      },
    });
  };

  const executeClearHistory = async () => {
    setBusyKey('clear-history');

    try {
      const response = await requestJson('/api/jobs', { method: 'DELETE' });
      setJobs(response.jobs || []);
      handleNotification(response.message || 'Print history cleared.');
      const nextHealth = await requestJson('/health');
      setHealth(nextHealth);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (clearError) {
      handleNotification(clearError.message, 'error');
    } finally {
      setBusyKey('');
    }
  };

  const askClearHistoryConfirmation = () => {
    openConfirmDialog({
      title: 'Clear print history',
      description: 'Remove all saved job history from the backend?',
      confirmLabel: 'Clear History',
      intent: 'danger',
      icon: 'history',
      details: [
        `Saved jobs: ${jobs.length}`,
        'Uploaded files will remain available.',
      ],
      onConfirm: async () => {
        closeConfirmDialog();
        await executeClearHistory();
      },
    });
  };

  const executeCancelJob = async (jobId) => {
    setBusyKey(`cancel:${jobId}`);

    try {
      const response = await requestJson(`/api/jobs/${encodeURIComponent(jobId)}/cancel`, {
        method: 'POST',
      });
      setJobs(response.jobs || []);
      handleNotification(response.message || `Job ${jobId} canceled.`);
      const nextHealth = await requestJson('/health');
      setHealth(nextHealth);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (cancelError) {
      handleNotification(cancelError.message, 'error');
    } finally {
      setBusyKey('');
    }
  };

  const askCancelConfirmation = (job) => {
    openConfirmDialog({
      title: 'Cancel print job',
      description: `Cancel job ${job.job_id || 'Pending'} for "${job.original_name}"?`,
      confirmLabel: 'Cancel Job',
      intent: 'danger',
      icon: 'cancel',
      details: [
        `Job ID: ${job.job_id || 'Pending'}`,
        `Status: ${job.status_label || job.status || 'Queued'}`,
        `Pages: ${job.page_range || 'All pages'}`,
      ],
      onConfirm: async () => {
        closeConfirmDialog();
        await executeCancelJob(job.job_id);
      },
    });
  };

  const openFile = (downloadPath) => {
    window.open(`${BASE_URL}${downloadPath}`, '_blank', 'noopener,noreferrer');
  };

  const surfacePaperSx = {
    backgroundColor: 'background.paper',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 18px 48px rgba(0,0,0,0.22)',
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Container maxWidth={false} sx={{ py: 2.5, px: { xs: 1.5, sm: 2.5 } }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ alignItems: 'stretch' }}>
            <Paper sx={{ ...surfacePaperSx, p: { xs: 1.5, sm: 2 }, flex: 1.35 }}>
              <Stack spacing={0.75}>
                <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 1.2 }}>
                  Printer
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: health?.status === 'ok' ? 'success.main' : 'error.main',
                      boxShadow: health?.status === 'ok'
                        ? '0 0 0 5px rgba(76, 175, 80, 0.15)'
                        : '0 0 0 5px rgba(244, 67, 54, 0.12)',
                    }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {health?.printer || 'Unknown'}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Auto refresh every 10 seconds
                </Typography>
              </Stack>
            </Paper>

            <Paper sx={{ ...surfacePaperSx, p: { xs: 1.5, sm: 2 }, minWidth: { md: 170 } }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 1.2 }}>
                Uploaded Files
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.8, fontWeight: 700 }}>
                {health?.uploaded_files ?? files.length}
              </Typography>
            </Paper>

            <Paper sx={{ ...surfacePaperSx, p: { xs: 1.5, sm: 2 }, minWidth: { md: 170 } }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 1.2 }}>
                Saved Jobs
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.8, fontWeight: 700 }}>
                {health?.saved_jobs ?? jobs.length}
              </Typography>
            </Paper>
          </Stack>

          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-start">
            <Paper sx={{ ...surfacePaperSx, p: { xs: 1.5, sm: 2 }, flex: 1, width: '100%' }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Upload Queue
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Accepted formats: PDF, PNG, JPG, JPEG, WEBP.
                  </Typography>
                </Box>

                <Stack
                  component="form"
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  onSubmit={handleUpload}
                >
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUploadRoundedIcon />}
                    fullWidth={isMobile}
                    sx={{ justifyContent: 'flex-start', flex: 1 }}
                  >
                    {uploadFile ? uploadFile.name : 'Choose file'}
                    <input
                      id="printer-upload-input"
                      hidden
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.webp"
                      onChange={(event) => setUploadFile(event.target.files?.[0] || null)}
                    />
                  </Button>
                  <Button type="submit" variant="contained" disabled={uploading} fullWidth={isMobile}>
                    {uploading ? 'Uploading...' : 'Upload'}
                  </Button>
                </Stack>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

                {loading ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Loading uploaded files...
                  </Typography>
                ) : null}

                {!loading && files.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No uploaded files yet.
                  </Typography>
                ) : null}

                <Stack spacing={1.25}>
                  {files.map((file) => {
                    const form = getPrintForm(file.stored_name);
                    const isExpanded = expandedFile === file.stored_name;
                    const isPrinting = busyKey === `print:${file.stored_name}`;
                    const isDeleting = busyKey === `delete:${file.stored_name}`;

                    return (
                      <Paper
                        key={file.stored_name}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderColor: 'rgba(255,255,255,0.08)',
                          backgroundColor: 'rgba(255,255,255,0.02)',
                        }}
                      >
                        <Stack spacing={1.25}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {file.original_name}
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                <Chip size="small" label={file.kind.toUpperCase()} />
                                <Chip size="small" label={formatBytes(file.size)} />
                                <Chip size="small" label={file.updated_at} />
                              </Stack>
                            </Box>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<FolderOpenRoundedIcon />}
                                onClick={() => openFile(file.download_path)}
                              >
                                Open
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setExpandedFile(isExpanded ? '' : file.stored_name)}
                              >
                                {isExpanded ? 'Hide Options' : 'Options'}
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<PrintRoundedIcon />}
                                onClick={() => askPrintConfirmation(file)}
                                disabled={isPrinting}
                              >
                                {isPrinting ? 'Printing...' : 'Print'}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteOutlineRoundedIcon />}
                                onClick={() => askDeleteConfirmation(file)}
                                disabled={isDeleting}
                              >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                              </Button>
                            </Stack>
                          </Stack>

                          {isExpanded ? (
                            <Stack spacing={1.25}>
                              <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                                <TextField
                                  label="Copies"
                                  type="number"
                                  size="small"
                                  inputProps={{ min: 1 }}
                                  value={form.copies}
                                  onChange={(event) => updatePrintForm(file.stored_name, 'copies', event.target.value)}
                                  fullWidth
                                />
                                <TextField
                                  label="Page range"
                                  size="small"
                                  placeholder="1-10 or 1,3,5-7"
                                  value={form.page_range}
                                  onChange={(event) => updatePrintForm(file.stored_name, 'page_range', event.target.value)}
                                  fullWidth
                                />
                              </Stack>
                              <TextField
                                label="Color mode"
                                size="small"
                                select
                                value={form.color_mode}
                                onChange={(event) => updatePrintForm(file.stored_name, 'color_mode', event.target.value)}
                                fullWidth
                              >
                                <MenuItem value="RGB">Color</MenuItem>
                                <MenuItem value="Gray">Black and White</MenuItem>
                              </TextField>
                            </Stack>
                          ) : null}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </Stack>
            </Paper>

            <Paper sx={{ ...surfacePaperSx, p: { xs: 1.5, sm: 2 }, flex: 1.05, width: '100%' }}>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Jobs
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Queue and local print history from the backend.
                    </Typography>
                  </Box>

                  <Stack spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {isAutoRefreshing ? <CircularProgress size={14} thickness={6} /> : null}
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Last updated: {lastUpdated || 'Starting...'}
                      </Typography>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<RefreshRoundedIcon />}
                        onClick={() => refreshJobsSnapshot()}
                        disabled={busyKey === 'refresh-jobs'}
                      >
                        {busyKey === 'refresh-jobs' ? 'Refreshing...' : 'Refresh'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<HistoryRoundedIcon />}
                        onClick={askClearHistoryConfirmation}
                        disabled={busyKey === 'clear-history'}
                      >
                        {busyKey === 'clear-history' ? 'Clearing...' : 'Clear History'}
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>

                {loading ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Loading jobs...
                  </Typography>
                ) : null}

                {!loading && jobs.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No print jobs yet.
                  </Typography>
                ) : null}

                <Stack spacing={1.25}>
                  {jobs.map((job) => {
                    const isProcessing = job.status_css === 'processing';
                    const isCanceling = busyKey === `cancel:${job.job_id}`;

                    return (
                      <Paper
                        key={`${job.job_id || job.created_at}-${job.original_name}`}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderColor: 'rgba(255,255,255,0.08)',
                          backgroundColor: 'rgba(255,255,255,0.02)',
                        }}
                      >
                        <Stack spacing={1.25}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                            <Box>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Chip
                                  size="small"
                                  label={job.status_label || job.status || 'Queued'}
                                  color={getStatusColor(job.status_css)}
                                />
                                {isProcessing ? (
                                  <Stack direction="row" spacing={0.8} alignItems="center">
                                    <CircularProgress size={16} thickness={6} />
                                    <Typography variant="caption" sx={{ color: 'warning.main' }}>
                                      Processing
                                    </Typography>
                                  </Stack>
                                ) : null}
                              </Stack>
                              <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 700 }}>
                                {job.original_name}
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                <Chip size="small" label={`Job ID: ${job.job_id || 'Pending'}`} />
                                <Chip size="small" label={`Copies: ${job.copies}`} />
                                <Chip size="small" label={job.color_mode === 'Gray' ? 'Black and White' : 'Color'} />
                              </Stack>
                            </Box>

                            {job.can_cancel ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<CancelPresentationRoundedIcon />}
                                onClick={() => askCancelConfirmation(job)}
                                disabled={isCanceling}
                              >
                                {isCanceling ? 'Canceling...' : 'Cancel Job'}
                              </Button>
                            ) : null}
                          </Stack>

                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip size="small" label={`Submitted: ${job.created_at}`} />
                            <Chip size="small" label={`Updated: ${job.updated_at}`} />
                            <Chip size="small" label={`Pages: ${job.page_range || 'All pages'}`} />
                            <Chip size="small" label={`Stored: ${job.stored_name}`} />
                          </Stack>

                          {job.message ? (
                            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                              {job.message}
                            </Typography>
                          ) : null}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Stack>

        <Dialog
          open={confirmState.open}
          onClose={closeConfirmDialog}
          maxWidth="xs"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              backgroundColor: '#15161b',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: confirmState.intent === 'danger'
                    ? 'rgba(244,67,54,0.14)'
                    : 'rgba(144,202,249,0.14)',
                  color: confirmState.intent === 'danger' ? 'error.main' : 'primary.main',
                }}
              >
                {getConfirmIcon(confirmState.icon)}
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                  {confirmState.intent === 'danger' ? 'Needs Confirmation' : 'Ready to Proceed'}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {confirmState.title}
                </Typography>
              </Box>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              {confirmState.description}
            </Typography>
            <Stack spacing={1}>
              {confirmState.details.map((detail) => (
                <Paper
                  key={detail}
                  variant="outlined"
                  sx={{
                    px: 1.25,
                    py: 1,
                    borderColor: 'rgba(255,255,255,0.06)',
                    backgroundColor: 'rgba(255,255,255,0.02)',
                  }}
                >
                  <Typography variant="body2">{detail}</Typography>
                </Paper>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={closeConfirmDialog} variant="text" color="inherit">
              Back
            </Button>
            <Button
              onClick={() => confirmState.onConfirm?.()}
              variant="contained"
              color={confirmState.intent === 'danger' ? 'error' : 'primary'}
            >
              {confirmState.confirmLabel}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </ThemeProvider>
  );
}

export default PrinterDashboard;
